import React, { useEffect, useState } from "react";
import { Input } from "@/components/ui/input";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { StickyNote, Pencil, Check, X, Eraser } from "lucide-react";

export const FAVORITE_NOTE_MAX = 200;

// Private admin note on a favourite church (« pourquoi je la suis ») — inline editor, Enter saves, Escape cancels.
export const FavoriteNote = ({ churchId, note = "", onSaved, className = "" }) => {
  const [editing, setEditing] = useState(false);
  const [val, setVal] = useState(note);
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (!editing) setVal(note); }, [note, editing]);

  const save = async (text) => {
    setBusy(true);
    try {
      const { data } = await api.put(`/admin/churches/${churchId}/favorite/note`, { note: text });
      onSaved?.(data.note);
      setEditing(false);
      toast.success(data.note ? "Note enregistrée" : "Note effacée");
    } catch (e) { toast.error(e?.response?.data?.detail || e?.friendlyMessage || "Erreur"); }
    finally { setBusy(false); }
  };

  if (editing) {
    return (
      <div className={`flex items-center gap-1.5 ${className}`} data-testid={`church-fav-note-editor-${churchId}`}>
        <Input autoFocus value={val} maxLength={FAVORITE_NOTE_MAX} placeholder="Pourquoi je suis cette église…" className="h-8 text-xs"
          onChange={(e) => setVal(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); save(val); } if (e.key === "Escape") setEditing(false); }}
          data-testid={`church-fav-note-input-${churchId}`} />
        <span className="text-[10px] tabular-nums shrink-0" style={{ color: "var(--yalola-text-muted)" }} data-testid={`church-fav-note-count-${churchId}`}>{val.length}/{FAVORITE_NOTE_MAX}</span>
        <button type="button" disabled={busy} onClick={() => save(val)} className="h-7 w-7 rounded-full grid place-items-center shrink-0" style={{ background: "var(--yalola-primary)", color: "#fff" }} title="Enregistrer" aria-label="Enregistrer la note" data-testid={`church-fav-note-save-${churchId}`}><Check size={13} /></button>
        {note && <button type="button" disabled={busy} onClick={() => save("")} className="h-7 w-7 rounded-full grid place-items-center shrink-0 hover:bg-black/5" title="Effacer la note" aria-label="Effacer la note" data-testid={`church-fav-note-clear-${churchId}`}><Eraser size={13} style={{ color: "#B91C1C" }} /></button>}
        <button type="button" onClick={() => setEditing(false)} className="h-7 w-7 rounded-full grid place-items-center shrink-0 hover:bg-black/5" title="Annuler" aria-label="Annuler" data-testid={`church-fav-note-cancel-${churchId}`}><X size={13} /></button>
      </div>
    );
  }
  return (
    <button type="button" onClick={() => setEditing(true)} className={`group inline-flex items-start gap-1.5 text-left text-xs rounded-md px-1.5 py-0.5 -ml-1.5 hover:bg-black/5 ${className}`}
      title={note ? "Modifier ma note privée" : "Ajouter une note privée (visible par toi seulement)"} data-testid={`church-fav-note-${churchId}`} data-has-note={note ? "1" : "0"}>
      <StickyNote size={12} className="mt-0.5 shrink-0" style={{ color: "#E0A63C" }} />
      {note
        ? <span className="italic" style={{ color: "var(--yalola-text)" }} data-testid={`church-fav-note-text-${churchId}`}>{note}</span>
        : <span style={{ color: "var(--yalola-text-muted)" }}>Ajouter une note privée</span>}
      <Pencil size={11} className="mt-0.5 opacity-0 group-hover:opacity-70 shrink-0" style={{ color: "var(--yalola-text-muted)" }} />
    </button>
  );
};
